/**
 * Campaign Configuration hook.
 *
 * `useCampaignConfiguration` — in-component hook that fetches campaign
 * configuration with loading state. Renders the page immediately with a
 * spinner while data loads.
 * The hook accepts a URL slug (e.g. 'cashback', 'no-cost-emi') and translates it
 * to the corresponding CampaignType enum value via SLUG_TO_CAMPAIGN_TYPE before
 * making the API call. This keeps the URL structure decoupled from the API contract.
 */

import { useState, useEffect, useRef } from 'react';
import { CampaignType, OperationType } from 'src/@types/valueMatching/enums';
import { CampaignConfigurationData } from 'src/@types/valueMatching/campaignConfiguration';
import { fetchCampaignConfiguration } from 'src/accessors/valueMatchingAccessor';
import { SLUG_TO_CAMPAIGN_TYPE } from 'src/routes/valueMatching/routes';
import { logError } from 'src/utils/monitoring';

/** Generic fallback message shown to users when an API error occurs */
const GENERIC_ERROR_MESSAGE = 'Failed to load campaign configuration. Please try again later.';

/** Shape of the data returned by useCampaignConfiguration */
export interface UseCampaignConfigurationResult {
  /** Campaign configuration data (null until loaded) */
  config: CampaignConfigurationData | null;
  /** Whether the fetch is in progress */
  loading: boolean;
  /** Error message (null on success) */
  error: string | null;
  /** The resolved CampaignType enum value */
  campaignType: CampaignType | undefined;
}

/**
 * Hook that fetches campaign configuration based on a URL slug.
 * Renders the page immediately and shows a loading spinner while data loads.
 *
 * The slug is the URL path segment (e.g. 'cashback', 'no-cost-emi').
 * It is translated to a CampaignType enum value via SLUG_TO_CAMPAIGN_TYPE,
 * which is then sent to the API.
 *
 * @param slug - URL slug from route params (e.g. 'cashback', 'no-cost-emi').
 *               Pass `undefined` or an empty string if the param is not yet available.
 * @param operationType - The operation type (e.g., CREATE_CAMPAIGN or MODIFY_CAMPAIGN)
 * @returns Configuration data, loading state, campaign type, and error state
 *
 * @example
 * ```tsx
 * const { campaignType: slug } = useParams();
 * const { config, loading, error, campaignType } = useCampaignConfiguration(slug, OperationType.CREATE_CAMPAIGN);
 * ```
 */
export function useCampaignConfiguration(
  slug: string | undefined,
  operationType: OperationType,
): UseCampaignConfigurationResult {
  const safeSlug = slug ?? '';
  // Translate URL slug → CampaignType enum using the centralized config
  const campaignType = SLUG_TO_CAMPAIGN_TYPE[safeSlug];

  const [config, setConfig] = useState<CampaignConfigurationData | null>(null);
  // Only show loading spinner when we have a valid campaign type to fetch.
  // Initialising to false for invalid/unknown slugs avoids a spurious spinner flash.
  const [loading, setLoading] = useState(!!campaignType);
  const [error, setError] = useState<string | null>(null);

  // Use ref to track the AbortController for proper request cancellation on cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!campaignType) {
      setError(`Unknown campaign type: ${safeSlug}`);
      setLoading(false);
      setConfig(null);
      return;
    }

    // Cancel any in-flight request from a previous render
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let cancelled = false;

    const loadConfig = async () => {
      setLoading(true);
      setError(null);
      setConfig(null); // clear stale data from previous campaign type
      try {
        const response = await fetchCampaignConfiguration({
          campaignType,
          operationType,
          signal: abortController.signal,
        });
        if (!cancelled) {
          const configData = response?.campaignConfigurationData;
          if (!configData) {
            throw new Error('Campaign configuration data missing from API response');
          }
          setConfig(configData);
        }
      } catch (err) {
        if (!cancelled) {
          setConfig(null);
          const message = err instanceof Error ? err.message : String(err);
          logError(message, { methodName: 'useCampaignConfiguration' });
          setError(GENERIC_ERROR_MESSAGE);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [campaignType, operationType, safeSlug]);

  return { config, loading, error, campaignType };
}
